import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk, RootState } from "../../app/store";
import client from "../../api/client";
import { Color, Game, Shape, Grid as ApiGrid } from "../../api/models";

interface Player {
    id: string
    name: string
    score: number
    isMe: boolean
    isTurn: boolean
}

interface Grid<T> {
    [row: number]: {
        [col: number]: T
    }
}

interface Tile {
    color: Color
    shape: Shape
}

interface HandTile extends Tile {
    id: number
    placement?: {
        row: number
        col: number
    }
    isTraded: boolean
}

interface DisplayTile extends Tile {
    id?: number
}

interface GameState {
    gameId?: string
    gameVersion: number
    isLoading: boolean
    loadingError?: string
    players: Player[]
    grid: Grid<Tile>
    hand: HandTile[]
    bagSize: number
    isStarted: boolean
    isOver: boolean
    myIdByGameId: { [gameId: string]: string }
}

export const initialState: GameState = {
    gameVersion: 0,
    isLoading: true,
    players: [],
    grid: {},
    hand: [],
    bagSize: 0,
    isStarted: false,
    isOver: false,
    myIdByGameId: {},
}

export const gameSlice = createSlice({
    name: 'game',
    initialState,
    reducers: {
        openGame: (state: GameState, { payload }: PayloadAction<{ gameId: string }>) => {
            return {
                ...initialState,
                gameId: payload.gameId,
                gameVersion: 0,
                myIdByGameId: state.myIdByGameId,   
            }
        },
        loadingFailed: (state: GameState, { payload }: PayloadAction<{ message: string }>) => {
            state.loadingError = payload.message
        },
        receiveGame: (state: GameState, { payload }: PayloadAction<{ game: Game }>) => {
            const game = payload.game

            if (state.gameId !== game.id) return // not current game

            if (state.gameVersion === game.version) return // no change

            const myPlayerId = state.myIdByGameId[game.id]

            const turnPlayerId = game.turnPlayer?.id

            state.isLoading = false
            state.players = game.players.map(p => ({
                ...p, 
                isTurn: p.id === turnPlayerId,
                isMe: p.id === myPlayerId,
            }))
            state.grid = game.grid
            state.bagSize = game.bagSize
            state.isStarted = game.isStarted
            state.isOver = game.isOver

            const isMyTurn = !!state.players.find(_ => _.isMe)?.isTurn
            
            let hand: HandTile[] = []
            let tiles = game.players.find(_ => _.id === myPlayerId)?.hand || []
            for (let t of state.hand) {
                const idx = tiles.findIndex(_ => _.color === t.color && _.shape === t.shape)
                if (idx === -1) continue // not in hand anymore
                tiles?.splice(idx, 1)
                if (!isMyTurn) t = { ...t, isTraded: false, placement: undefined }
                hand.push(t)
            }
            for (let t of tiles) {
                hand.push({ ...t, id: genId(), isTraded: false })
            }
            state.hand = hand
        },
        addPlayerSuccess: (state: GameState, action: PayloadAction<{ gameId: string, playerId: string }>) => {
            const { gameId, playerId } = action.payload
            state.myIdByGameId = { ...state.myIdByGameId, [gameId]: playerId }
        },
        placeTile: (state: GameState, { payload: { row, col, id } }: PayloadAction<{ row: number, col: number, id: number }>) => {
            if (!state.players.find(_ => _.isMe)?.isTurn) return // not your turn...
            const hand = state.hand
            const tile = hand.find(_ => _.id === id)

            if (!tile) return // tile not in hand

            if (state.hand.some(_ => _.isTraded)) return // in trade mode

            tile.placement = { row, col }
            tile.isTraded = false
            state.hand = hand
        },  
        takeBackTile: (state: GameState, { payload: { id } }: PayloadAction<{ id: number }>) => {
            if (!state.players.find(_ => _.isMe)?.isTurn) return // not your turn...
            const hand = state.hand;
            const tile = hand.find(_ => _.id === id);
            if (!tile) return // tile not found
            tile.placement = undefined;
            tile.isTraded = false;
            state.hand = hand;
        },
        tradeTile: (state: GameState, { payload: { id } }: PayloadAction<{ id: number }>) => {
            if (!state.players.find(_ => _.isMe)?.isTurn) return // not your turn...
            const hand = state.hand
            const tile = hand.find(_ => _.id === id)

            if (!tile) return // tile not in hand
            
            if (state.hand.some(_ => !!_.placement)) return // in place mode

            tile.placement = undefined
            tile.isTraded = true
            state.hand = hand
        },
        resetMyTurn: (state: GameState) => {
            state.hand = state.hand.map(tile => ({
                ...tile,
                placement: undefined,
                isTraded: false,
            }))
        }
    }
})

// private actions
const {
    addPlayerSuccess,
    loadingFailed,
} = gameSlice.actions

// public actions
export const {
    openGame,
    placeTile,
    takeBackTile,
    tradeTile,
    resetMyTurn,
    receiveGame,
} = gameSlice.actions


export const loadGame = (gameId: string): AppThunk => dispatch => {
    dispatch(openGame({ gameId }))
    client.getGame(gameId).then(
        game => dispatch(receiveGame({ game })),
        e => dispatch(loadingFailed(e))
    )
}

export const refreshGame = (gameId: string): AppThunk => dispatch => {
    client.getGame(gameId).then(
        game => dispatch(receiveGame({ game })),
        e => {}
    )
}

export const createGame = (onSuccess?: (gameId: string) => void): AppThunk => dispatch => {
    client.newGame()
        .then(game => {
            dispatch(receiveGame({ game }))
            if (onSuccess) onSuccess(game.id)
        })
}

export const addPlayer = ({ name }: { name: string }): AppThunk => (dispatch, getState) => {
    const gameId = getGameId(getState())
    if (!gameId) return;
    client.addPlayer(gameId, name)
        .then(_ => dispatch(addPlayerSuccess({ gameId, playerId: _.id })))
}

export const addBot = ({ name }: { name: string }): AppThunk => (_, getState) => {
    const gameId = getGameId(getState())
    if (!gameId) return;
    return client.addPlayer(gameId, name, true)
}

export const startGame = (): AppThunk => (dispatch, getState) => {
    const gameId = getGameId(getState())
    if (!gameId) return;
    client.startGame(gameId).then(
        game => dispatch(receiveGame({ game })),
        (e) => alert(e)
    )
}

export const finishMyTurn = (): AppThunk => (dispatch, getState) => {
    const state = getState()
    const gameId = getGameId(state)
    const isMyTurn = getIsMyTurn(state)

    if (!gameId || !isMyTurn) return
    
    const placedTiles = getPlacedTiles(state)
    if (placedTiles.length !== 0)  {
        let placement: ApiGrid = {}
        for (let tile of placedTiles) {
            const { row, col } = tile.placement!
            placement = { ...placement, [row]: { ...placement[row], [col]: tile } }
        }
        return client.placeTiles(gameId, placement).then(
            game => {
                dispatch(receiveGame({ game }))
                dispatch(resetMyTurn())
            },
            e => alert(e)
        )
    }
    const tradedTiles = getTradedTiles(state)
    if (tradedTiles.length !== 0) {
        client.tradeTiles(gameId, tradedTiles).then(
            game => {
                dispatch(receiveGame({ game }))
                dispatch(resetMyTurn())
            },
            e => alert(e)
        )
    }
}

export const gameReducer = gameSlice.reducer

export const getIsLoading = (state: RootState) => state.game.isLoading
export const getLoadingError = (state: RootState) => state.game.loadingError
export const getGameId = (state: RootState) => state.game.gameId
export const getIsStarted = (state: RootState) => state.game.isStarted
export const getIsOver = (state: RootState) => state.game.isOver
export const getMe = (state: RootState) => state.game.players.find(_ => _.isMe)
export const getPlayers = (state: RootState) => state.game.players
export const getBagSize = (state: RootState) => state.game.bagSize
export const getIsMyTurn = (state: RootState) => state.game.players.some(p => p.isTurn && p.isMe)
export const getHandTiles = (state: RootState) => state.game.hand.filter(t => !t.isTraded && !t.placement)
export const getTradedTiles = (state: RootState) => state.game.hand.filter(t => t.isTraded)
export const getPlacedTiles = (state: RootState) => state.game.hand.filter(t => !!t.placement)
export const getGrid = (state: RootState) => {
    let grid: Grid<DisplayTile> = {}
    // copy the grid
    for (let row in state.game.grid) {
        for (let col in state.game.grid[row]) {
            const tile = state.game.grid[row][col]
            grid = { ...grid, [row]: { ...grid[row], [col]: tile } }
        }
    }
    // add placed tiles from hand
    for (let tile of state.game.hand) {
        if (!tile.placement) continue
        const { row, col } = tile.placement
        grid = { ...grid, [row]: { ...grid[row], [col]: tile } }
    }
    return grid
}


let __tile__id__ = 1
function genId(): number {
    return __tile__id__++
}